import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Badge,
  IconButton,
  Button,
  useTheme,
  alpha,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useDroppable } from '@dnd-kit/core';
import { styled } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import TaskCard from './TaskCard';
import AssignmentIcon from '@mui/icons-material/Assignment';

const columnConfig = {
  today: {
    title: 'Tasks Today',
    color: '#1976d2',
    bgColor: '#e3f2fd'
  },
  this_week: {
    title: 'Tasks This Week',
    color: '#ed6c02',
    bgColor: '#fff3e0'
  },
  this_month: {
    title: 'Tasks This Month',
    color: '#9c27b0',
    bgColor: '#f3e5f5'
  },
  later: {
    title: 'Tasks Later',
    color: '#2e7d32',
    bgColor: '#e8f5e8'
  },
  completed: {
    title: 'Completed Tasks',
    color: '#616161',
    bgColor: '#f5f5f5'
  }
};

const StyledColumnPaper = styled(Paper)(({ theme, isover, columncolor, iscollapsed, iscompletedcolumn }) => ({
  padding: theme.spacing(3),
  height: 'calc(100vh - 280px)',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  border: isover 
    ? `2px solid ${columncolor}`
    : `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
  background: isover 
    ? `linear-gradient(135deg, ${alpha(columncolor, 0.12)}, ${alpha(columncolor, 0.04)})`
    : `linear-gradient(135deg, ${alpha('#ffffff', 1)}, ${alpha('#fafafa', 0.8)})`,
  boxShadow: isover
    ? `0 16px 40px ${alpha(columncolor, 0.2)}`
    : `0 4px 20px ${alpha(theme.palette.grey[500], 0.08)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  minWidth: iscollapsed && iscompletedcolumn ? 70 : 300,
  maxWidth: iscollapsed && iscompletedcolumn ? 90 : 'none',
  '&:hover': {
    transform: iscollapsed && iscompletedcolumn ? 'none' : 'translateY(-4px)',
    boxShadow: iscollapsed && iscompletedcolumn 
      ? `0 4px 20px ${alpha(theme.palette.grey[500], 0.08)}`
      : `0 8px 32px ${alpha(theme.palette.grey[500], 0.15)}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${columncolor}, ${alpha(columncolor, 0.7)})`,
    opacity: isover ? 1 : 0.9
  }
}));

const TaskColumn = ({ 
  columnId, 
  tasks, 
  onTaskEdit, 
  onTaskDelete, 
  onTaskComplete, 
  onTaskAssign, 
  onAddTask,
  users,
  currentUser,
  timeFormat = '24hour',
  isCollapsed = false,
  onToggleCollapse
}) => {
  const theme = useTheme();
  const { setNodeRef, isOver } = useDroppable({
    id: columnId,
  });

  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickTaskTitle, setQuickTaskTitle] = useState('');
  const [quickTaskDeadline, setQuickTaskDeadline] = useState(null);
  const [quickTaskPriority, setQuickTaskPriority] = useState('medium');

  const config = columnConfig[columnId];
  const taskCount = tasks.length;
  const isCompletedColumn = columnId === 'completed';

  const handleQuickAdd = () => {
    if (!quickTaskTitle.trim()) return;
    
    const quickTaskData = {
      title: quickTaskTitle.trim(),
      description: '',
      priority: quickTaskPriority,
      assigned_to: currentUser?.id,
      due_date: quickTaskDeadline ? quickTaskDeadline.toISOString() : null,
      start_date: null,
      column_type: columnId,
      tags: [],
      estimated_hours: null,
      status: isCompletedColumn ? 'completed' : 'todo'
    };
    
    onAddTask(quickTaskData);
    setQuickTaskTitle('');
    setQuickTaskDeadline(null);
    setQuickTaskPriority('medium');
    setQuickAddOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledColumnPaper
        ref={setNodeRef}
        isover={isOver}
        columncolor={config.color}
        iscollapsed={isCollapsed}
        iscompletedcolumn={isCompletedColumn}
        sx={{
          minWidth: isCompletedColumn && isCollapsed ? 60 : 280,
          maxWidth: isCompletedColumn && isCollapsed ? 60 : 'none'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isCollapsed && isCompletedColumn ? 'center' : 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, justifyContent: isCollapsed && isCompletedColumn ? 'center' : 'flex-start' }}>
            {isCompletedColumn && onToggleCollapse && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={onToggleCollapse}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(config.color, 0.2)}`,
                    backgroundColor: alpha(config.color, 0.08),
                    color: config.color,
                    width: 32,
                    height: 32,
                    '&:hover': {
                      backgroundColor: alpha(config.color, 0.15),
                      transform: 'scale(1.1)',
                      boxShadow: `0 4px 12px ${alpha(config.color, 0.25)}`
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Typography sx={{ fontSize: 18, color: config.color, fontWeight: 500 }}>
                    {isCollapsed ? '>' : '<'}
                  </Typography>
                </IconButton>
                {isCollapsed && (
                  <Typography sx={{ 
                    fontSize: '1.5rem', 
                    color: config.color, 
                    fontWeight: 500,
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    transform: 'rotate(180deg)',
                    textAlign: 'center',
                  }}>
                    Completed
                  </Typography>
                )}
              </Box>
            )}
            {!isCollapsed && (
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 500,
                  color: config.color,
                  fontSize: '1.1rem',
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.02em'
                }}
              >
                {config.title}
              </Typography>
            )}
            {!isCollapsed && (
              <Badge 
                badgeContent={taskCount} 
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: config.color,
                    color: 'white',
                    fontWeight: 500,
                    borderRadius: 3,
                    fontSize: '0.75rem',
                    minWidth: 22,
                    height: 22,
                    boxShadow: `0 2px 8px ${alpha(config.color, 0.3)}`
                  }
                }}
              />
            )}
          </Box>
        </Box>

        {isCollapsed && isCompletedColumn ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: 3,
            cursor: 'pointer',
            '&:hover': {
              transform: 'scale(1.05)'
            },
            transition: 'transform 0.2s ease'
          }}
          onClick={onToggleCollapse}
          >

          </Box>
        ) : (
          <Collapse in={!isCollapsed} orientation={isCompletedColumn ? "horizontal" : "vertical"}>
            <Box>
              {!isCompletedColumn && !quickAddOpen ? (
                <Button
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  variant="outlined"
                  fullWidth
                  sx={{ 
                    mb: 3,
                    borderRadius: 4,
                    borderColor: alpha(config.color, 0.3),
                    color: config.color,
                    backgroundColor: alpha(config.color, 0.04),
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    border: `1px solid ${alpha(config.color, 0.2)}`,
                    py: 1.5,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: config.color,
                      backgroundColor: alpha(config.color, 0.08),
                      boxShadow: `0 4px 16px ${alpha(config.color, 0.15)}`,
                      transform: 'translateY(-1px)'
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setQuickAddOpen(true)}
                >
                  Add Task
                </Button>
              ) : !isCompletedColumn && quickAddOpen ? (
                <Box sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${alpha(config.color, 0.4)}`,
                  backgroundColor: 'white',
                  boxShadow: `0 4px 12px ${alpha(config.color, 0.15)}`
                }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                      To-do Title
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter task title..."
                      value={quickTaskTitle}
                      onChange={(e) => setQuickTaskTitle(e.target.value)}
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          borderRadius: 2
                        },
                        '& .MuiOutlinedInput-root': {
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                      Deadline
                    </Typography>
                    <DateTimePicker
                      value={quickTaskDeadline}
                      onChange={(newValue) => setQuickTaskDeadline(newValue)}
                      format={timeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                      ampm={timeFormat !== '24hour'}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiInputBase-root': {
                              borderRadius: 2
                            },
                            '& .MuiOutlinedInput-root': {
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderWidth: '1px'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderWidth: '1px'
                              }
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                      Priority
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={quickTaskPriority}
                        onChange={(e) => setQuickTaskPriority(e.target.value)}
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '1px'
                          }
                        }}
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="urgent">Urgent</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      onClick={handleQuickAdd}
                      disabled={!quickTaskTitle.trim()}
                      sx={{
                        borderRadius: 2,
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        px: 2,
                        py: 1,
                        bgcolor: config.color,
                        color: 'white',
                        '&:hover': {
                          bgcolor: config.color
                        },
                        '&:disabled': {
                          bgcolor: alpha(theme.palette.grey[400], 0.5),
                          color: theme.palette.grey[600]
                        }
                      }}
                    >
                      Add
                    </Button>
                    <Button
                      onClick={() => {
                        setQuickAddOpen(false);
                        setQuickTaskTitle('');
                        setQuickTaskDeadline(null);
                        setQuickTaskPriority('medium');
                      }}
                      sx={{
                        borderRadius: 2,
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        px: 2,
                        py: 1,
                        color: 'text.secondary'
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : null}

              <Box 
                sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 1,
                  mr: -1,
                  contain: 'layout style paint',
                  maxHeight: isCompletedColumn ? '420px' : 'calc(100vh - 340px)',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha(config.color, 0.3),
                    borderRadius: '10px',
                    '&:hover': {
                      background: alpha(config.color, 0.5),
                    }
                  }
                }}
              >
                {tasks.map((task) => (
                  <Box key={task.id} sx={{ marginLeft: isCompletedColumn ? '30px' : '0px' }}>
                    <TaskCard
                      task={task}
                      onEdit={onTaskEdit}
                      onDelete={onTaskDelete}
                      onComplete={onTaskComplete}
                      onAssign={onTaskAssign}
                      users={users}
                    />
                  </Box>
                ))}
                
                                 {tasks.length === 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      height: '240px',
                      textAlign: 'left',
                      border: `2px dashed ${alpha(config.color, 0.2)}`,
                      borderRadius: 4,
                      backgroundColor: alpha(config.color, 0.02),
                      color: 'text.secondary',
                      p: 1.5,
                      ml: isCompletedColumn ? 2 : 0,
                      '&:hover': {
                        borderColor: alpha(config.color, 0.3),
                        backgroundColor: alpha(config.color, 0.04)
                      },
                      transition: 'all 0.2s ease',
                      mt: isCompletedColumn ? 9 : 0
                    }}
                  >
                   <Box sx={{
                     width: 48,
                     height: 48,
                     borderRadius: '50%',
                     backgroundColor: alpha(config.color, 0.1),
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     mb: 2
                   }}>
                     <AssignmentIcon sx={{ fontSize: 24, color: config.color }} />
                   </Box>
                   <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
                     {isCompletedColumn ? 'No completed tasks' : 'No tasks yet'}
                   </Typography>
                   <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                     {isCompletedColumn ? 'Completed tasks will appear here' : 'Add a task to get started'}
                   </Typography>
                 </Box>
               )}
              </Box>
            </Box>
          </Collapse>
        )}
      </StyledColumnPaper>
    </LocalizationProvider>
  );
};

export default TaskColumn;
